import { encodeSupportTicketFilterGroup } from "@redotech/effect-api-shared/schema/support-ticket-filters";
import { FeatureFlagKey } from "@redotech/feature-flags-model";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import Copy01Icon from "@redotech/redo-icons/copy-01.svg";
import Edit04Icon from "@redotech/redo-icons/edit-04.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  DefaultView,
  OPEN_VIEW_FILTER_GROUP,
} from "@redotech/redo-support-v2/query/default-views";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "@redotech/redo-web/arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Action0PortalContext } from "@redotech/redo-web/page";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { BulkSupportTicketActionProgressBar } from "../actions/bulk-support-ticket-action-progress-bar";
import { BulkSupportTicketActionsBar } from "../actions/bulk-support-ticket-actions-bar";
import { NewSupportTicketModal } from "../components/new-support-ticket-modal";
import { ActiveSupportTicketFilters } from "../filters/active-support-ticket-filters";
import { SupportTicketsFilterToolbar } from "../filters/support-tickets-filter-toolbar";
import { useViewManagement } from "../hooks/use-view-management";
import { invalidateSupportV2Enabled } from "../providers/use-support-v2-enabled";
import { SupportTicketsTable } from "../table/components/support-tickets-table";
import { useSupportTicketsTableContextMaybe } from "../table/support-tickets-table-context";
import * as styles from "./support-tickets-table-view.module.css";
import { SupportV2ViewModal } from "./views/support-v2-view-modal";

const PAGE_SIZE_OPTIONS: RedoListItem<number>[] = [25, 50, 100].map((size) => ({
  id: size,
  type: "text",
  text: `${size} per page`,
  value: size,
}));

const PaginationControls = memo(function PaginationControls() {
  const tableContext = useSupportTicketsTableContextMaybe();

  if (!tableContext) {
    return null;
  }

  const {
    currentPage,
    pageSize,
    setPageSize,
    totalCount,
    hasPreviousPage,
    hasNextPage,
    goToNextPage,
    goToPreviousPage,
  } = tableContext;

  const startIndex = currentPage * pageSize + 1;
  const endIndex = Math.min((currentPage + 1) * pageSize, totalCount);

  if (totalCount === 0) {
    return null;
  }

  return (
    <Flex align="center" gap="xs">
      <Flex className="w-32">
        <RedoSingleSelectDropdownInput
          flexGrow={false}
          fullWidth
          options={PAGE_SIZE_OPTIONS}
          optionSelected={({ item }) => setPageSize(item.value)}
          selectedItem={
            PAGE_SIZE_OPTIONS.find((option) => option.value === pageSize) ?? {
              id: pageSize,
              type: "text",
              text: `${pageSize} per page`,
              value: pageSize,
            }
          }
          size="xs"
        />
      </Flex>
      <RedoButton
        disabled={!hasPreviousPage}
        hierarchy="tertiary"
        IconLeading={ChevronLeftIcon}
        onClick={goToPreviousPage}
        size="sm"
      />
      <Text fontSize="xs" textColor="secondary">
        {startIndex}-{endIndex} of {totalCount}
      </Text>
      <RedoButton
        disabled={!hasNextPage}
        hierarchy="tertiary"
        IconLeading={ChevronRightIcon}
        onClick={goToNextPage}
        size="sm"
      />
    </Flex>
  );
});

export const SupportTicketsTableView = memo(function SupportTicketsTableView() {
  const navigate = useNavigate();
  const team = useRequiredContext(TeamContext);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const tableContext = useSupportTicketsTableContextMaybe();
  const [searchParams] = useSearchParams();
  const action0Portal = useContext(Action0PortalContext);

  const handleNavigateAfterCreate = useHandler(
    async ({ supportTicketId }: { supportTicketId: string }) => {
      navigate(`/stores/${team._id}/support-v2/${supportTicketId}`);
    },
  );

  // Context may be null during route transition animations
  if (!tableContext) {
    return null;
  }

  const { selectedSupportTicketCount } = tableContext;

  // Redirect to open default view only when landing on the bare route with no params
  if (
    !searchParams.get("view") &&
    !searchParams.get("defaultView") &&
    !searchParams.get("fg")
  ) {
    const params = new URLSearchParams();
    params.set("defaultView", DefaultView.OPEN);
    params.set("fg", encodeSupportTicketFilterGroup(OPEN_VIEW_FILTER_GROUP));
    return <Navigate replace to={`?${params.toString()}`} />;
  }

  return (
    <>
      {action0Portal &&
        createPortal(
          <Flex align="center" gap="sm">
            <SwitchToClassicButton />
            <RedoButton
              hierarchy="primary"
              IconLeading={PlusIcon}
              onClick={() => setCreateModalOpen(true)}
              text="Create new ticket"
            />
          </Flex>,
          action0Portal,
        )}
      <Flex
        className={styles.fadeIn}
        dir="column"
        gap="md"
        height="full"
        overflow="hidden"
        pb="none"
        pt="md"
        px="lg"
        w="full"
      >
        <Flex dir="column" gap="md">
          <Flex align="center" justify="space-between">
            <SupportTicketsFilterToolbar />
            {selectedSupportTicketCount > 0 ? (
              <BulkSupportTicketActionsBar />
            ) : (
              <Flex align="center" gap="sm">
                <PaginationControls />
                <ViewManagementButtons />
              </Flex>
            )}
          </Flex>
          <ActiveSupportTicketFilters />
        </Flex>
        <BulkSupportTicketActionProgressBar />
        <Flex className="min-h-0" grow={1} overflow="hidden">
          <SupportTicketsTable />
        </Flex>
        <NewSupportTicketModal
          onCreated={(supportTicketId) =>
            void handleNavigateAfterCreate({ supportTicketId })
          }
          open={createModalOpen}
          setOpen={setCreateModalOpen}
        />
      </Flex>
    </>
  );
});

const ViewManagementButtons = memo(function ViewManagementButtons() {
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const [editDropdownRef, setEditDropdownRef] = useState<HTMLElement | null>(
    null,
  );

  const {
    activeView,
    activeDefaultView,
    filtersMatchView,
    hasActiveFilters,
    reservedViewNames,
    reservedViewNamesExcludingCurrent,
    handleCreateView,
    handleUpdateViewFilters,
    handleEditViewName,
    handleDeleteView,
    handleCancel,
  } = useViewManagement();

  const deleteViewMenuItem: RedoCommandMenuItem = useMemo(
    () => ({
      Icon: TrashIcon,
      text: "Delete view",
      onClick: handleDeleteView,
      theme: "destructive" as const,
    }),
    [handleDeleteView],
  );

  const editViewNameMenuItem: RedoCommandMenuItem = useMemo(
    () => ({
      Icon: Edit04Icon,
      text: "Edit view name",
      onClick: () => setEditModalOpen(true),
    }),
    [],
  );

  const saveAsNewViewMenuItem: RedoCommandMenuItem = useMemo(
    () => ({
      Icon: Copy01Icon,
      text: "Save as new view",
      onClick: () => setSaveModalOpen(true),
    }),
    [],
  );

  // On a default view — nothing to show
  if (activeDefaultView) {
    return null;
  }

  // No active view with active filters — show "Save as new view" + "Cancel" inline
  if (!activeView && hasActiveFilters) {
    return (
      <>
        <Flex align="center" gap="sm">
          <RedoButton
            hierarchy="tertiary"
            onClick={handleCancel}
            size="sm"
            text="Reset"
          />
          <RedoButton
            hierarchy="secondary"
            onClick={() => setSaveModalOpen(true)}
            size="sm"
            text="Save as new view"
          />
        </Flex>
        <SupportV2ViewModal
          currentName=""
          isOpen={saveModalOpen}
          mode="create"
          onClose={() => setSaveModalOpen(false)}
          onSubmit={handleCreateView}
          reservedViewNames={reservedViewNames}
        />
      </>
    );
  }

  // No active view, no active filters — nothing to show
  if (!activeView) {
    return null;
  }

  // On a saved view with changed filters — "Update view" + dropdown
  if (!filtersMatchView) {
    return (
      <>
        <RedoButtonDropdown
          dropdownOpen={editDropdownOpen}
          onClick={handleUpdateViewFilters}
          ref={setEditDropdownRef}
          setDropdownOpen={setEditDropdownOpen}
          size="sm"
          text="Update view"
        >
          <RedoCommandMenu
            anchor={editDropdownRef}
            items={[
              saveAsNewViewMenuItem,
              editViewNameMenuItem,
              deleteViewMenuItem,
            ]}
            open={editDropdownOpen}
            setOpen={setEditDropdownOpen}
          />
        </RedoButtonDropdown>
        <SupportV2ViewModal
          currentName=""
          isOpen={saveModalOpen}
          mode="create"
          onClose={() => setSaveModalOpen(false)}
          onSubmit={handleCreateView}
          reservedViewNames={reservedViewNames}
        />
        {editModalOpen && (
          <SupportV2ViewModal
            currentName={activeView.name}
            isOpen={editModalOpen}
            mode="edit"
            onClose={() => setEditModalOpen(false)}
            onSubmit={async (name) => {
              await handleEditViewName(name);
              setEditModalOpen(false);
            }}
            reservedViewNames={reservedViewNamesExcludingCurrent}
          />
        )}
      </>
    );
  }

  // On a saved view with matching filters — "Edit view" + dropdown with delete
  return (
    <>
      <RedoButtonDropdown
        dropdownOpen={editDropdownOpen}
        onClick={() => setEditModalOpen(true)}
        ref={setEditDropdownRef}
        setDropdownOpen={setEditDropdownOpen}
        size="sm"
        text="Edit view"
      >
        <RedoCommandMenu
          anchor={editDropdownRef}
          items={[deleteViewMenuItem]}
          open={editDropdownOpen}
          setOpen={setEditDropdownOpen}
        />
      </RedoButtonDropdown>
      {editModalOpen && (
        <SupportV2ViewModal
          currentName={activeView.name}
          isOpen={editModalOpen}
          mode="edit"
          onClose={() => setEditModalOpen(false)}
          onSubmit={async (name) => {
            await handleEditViewName(name);
            setEditModalOpen(false);
          }}
          reservedViewNames={reservedViewNamesExcludingCurrent}
        />
      )}
    </>
  );
});

const SwitchToClassicButton = memo(function SwitchToClassicButton() {
  const navigate = useNavigate();
  const team = useRequiredContext(TeamContext);
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSwitchToClassic = useHandler(async () => {
    setIsLoading(true);
    try {
      await platformRpcClient.removeTeamFromFeatureFlag({
        flagKey: FeatureFlagKey.SUPPORT_HELPDESK_V2,
      });

      // Reset the shared flag query so support navigation re-fetches.
      invalidateSupportV2Enabled(String(team._id));

      navigate(`/stores/${team._id}/support/table/all`);
    } catch (error) {
      console.error("Failed to switch to classic experience:", error);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <RedoButton
      disabled={isLoading}
      hierarchy="tertiary"
      onClick={handleSwitchToClassic}
      size="sm"
      text="Switch back to the classic experience"
    />
  );
});
